<template>
  <aside class="sidebar" :class="{ 'sp-only': spOnly }">
    <!--
      マイページ系ルートの SP では共通の導線（マイページ／初めての方へ／よくあるご質問／
      お買い物ガイド）を出さず、マイページメニューに差し替える（下の `isMypageRoute`）。
    -->
    <PartsMypageSidebarNav v-if="showsMypageNav" />

    <template v-else>
      <div class="sidebar-btns">
        <NuxtLink :to="isLoggedIn ? '/mypage' : '/register'" class="sidebar-btn btn-regist">
          <div class="icon_member-regist">
            <img src="/image/common/header/icon_member-regist_white.png" alt="" />
          </div>
          {{ isLoggedIn ? 'マイページ' : '会員登録' }}
        </NuxtLink>
        <NuxtLink v-if="!isLoggedIn" to="/login" class="sidebar-btn btn-login">
          <div class="icon_login">
            <img src="/image/common/header/icon_login.png" alt="" />
          </div>
          ログイン
        </NuxtLink>
      </div>

      <ul class="sidebar-nav">
        <li v-for="item in SIDEBAR_NAV_ITEMS" :key="item.label">
          <NuxtLink :to="item.to">
            <div class="sidebar-nav_icon" :class="item.iconModifier">
              <img :src="item.icon" alt="" />
            </div>
            {{ item.label }}
          </NuxtLink>
        </li>
      </ul>
    </template>

    <PartsSidebarBanners />
  </aside>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `sidebar`。2カラムの左（PC）と、フッター直前の SP 版で同じものを使う。
 *
 * **マイページのサイドバーはこれとは別物**（`organisms/mypage/sidebar.vue`）。
 * 支給のマイページ7本は `sidebar-btns` と `sidebar-nav` を持たず、`my-page-sidebar-nav` を出す。
 * 共通なのはバナー4枚なので `PartsSidebarBanners` に切り出して両方から使う。
 *
 * **フッター上の版（`spOnly`）だけは、マイページ系ルートで中身をマイページメニューに差し替える。**
 * マイページの `.sidebar`（2カラム左）は支給CSS で 1000px 以下だと消えるため、素直に作ると
 * SP でマイページメニューが出ず、代わりにここの共通導線が出る。旧デザインは SP でも
 * マイページメニューを出していたので、**SP ではこちらが `my-page-sidebar-nav` を引き受ける**。
 * バナー4枚はマイページでも共通なのでそのまま出す。
 */
import { useAuth } from '@/composables/useAuth';
import { SIDEBAR_NAV_ITEMS } from '@/consts/ChromeConst';

type Props = {
  /**
   * フッター直前に置く版（`sidebar sp-only`）。
   *
   * 支給CSSのクラス名 `sp-only` に対応するが、`.sidebar.sp-only` に限っては
   * 1000px 以下で表示される（＝SP専用ではない）。共通ユーティリティの `.sp-only`
   * は 768px 以下という基準なので、クラス名と実挙動がずれている点に注意。
   * クラス名は支給CSS側の都合なのでこちらでは変えられない。
   */
  spOnly?: boolean;
};
const props = withDefaults(defineProps<Props>(), { spOnly: false });

const { currentUser } = useAuth();
const isLoggedIn = computed<boolean>(() => currentUser.value !== null);

const route = useRoute();

/**
 * マイページメニューを持つルートか。マイページ用サイドバー（`OrganismsMypageSidebar`）を
 * 置いている15本（`/mypage/**` 14本と `/customer-enquete`）と一致させる。
 * `/customer-enquete/[serialNumber]` はサイドバーを持たないので前方一致にしない。
 */
const isMypageRoute = computed<boolean>(
  () => route.path.startsWith('/mypage') || route.path.replace(/\/$/, '') === '/customer-enquete'
);

/** フッター上の版だけが差し替え対象。2カラム左（PC）の共通サイドバーは変えない */
const showsMypageNav = computed<boolean>(() => props.spOnly && isMypageRoute.value);
</script>
